import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  TreePine, 
  ArrowRight, 
  ExternalLink,
  ChevronLeft,
  Users,
  Gift,
  Sparkles,
  CheckCircle,
  Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { PhotoGallery } from '../components/PhotoGallery';
import { FestivalCountdown } from '../components/FestivalCountdown';
import { VendorSpotsFilled } from '../components/VendorSpotsFilled';

const eventDetails = {
  title: "Festival of Trees at the Barn",
  dates: "November 20-21, 2026",
  times: "10:00 AM - 6:00 PM (Both Days)",
  venue: "The Barn at Brambleton",
  address: "24439 Evergreen Mills Rd, Ashburn, VA 20148",
  venueWebsite: "https://www.thebarnatbrambleton.com",
};

const highlights = [
  {
    icon: TreePine,
    title: "Decorated Trees",
    description: "Marvel at beautifully decorated trees created by local artisans and community groups."
  },
  {
    icon: Gift,
    title: "Handmade Gifts",
    description: "Shop unique handcrafted items from our curated selection of 50+ artisan vendors."
  },
  {
    icon: Users,
    title: "Community Event",
    description: "A beloved annual tradition bringing together families from across Loudoun County."
  },
  {
    icon: Sparkles,
    title: "Holiday Magic",
    description: "Experience the festive atmosphere in a stunning restored 1940s dairy barn."
  },
];

const vendorBenefits = [
  "Prime booth location in a premier venue",
  "Access to thousands of holiday shoppers",
  "Marketing support and event promotion",
  "Professional event coordination",
  "Load-in/load-out assistance",
  "Indoor climate-controlled space",
];

const previousYearPhotos = [
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/0l7900yy_20241105_120630.jpg",
    caption: "Setting up the beautiful barn venue"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/0vgq1kv9_20241105_120637.jpg",
    caption: "Vendor booth area ready for setup"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/tqscd03z_20241106_160110%20%281%29.jpg",
    caption: "The market in full swing"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/k2l2ztq1_20241107_164038%20%282%29.jpg",
    caption: "Shoppers enjoying the artisan booths"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/rhk03ekx_20241108_165600%20%281%29.jpg",
    caption: "Handcrafted holiday ornaments"
  },
  // Batch 2
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/gci21dy6_20241108_165629%20%281%29.jpg",
    caption: "Adorable snowman treat pops"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/jvmharlz_20241108_165650%20%281%29.jpg",
    caption: "Vintage red truck holiday centerpiece"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/au5t1p0s_20241108_165741.jpg",
    caption: "Charming bear mugs gift sets"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/o70dqud5_20241108_165752.jpg",
    caption: "Winter fox ornament"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/k0ulovpw_20241108_165822%20%281%29.jpg",
    caption: "Handcrafted menorah ornament"
  },
  // Batch 3
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/z4dvh383_20241108_165855.jpg",
    caption: "Beautiful gemstone crystal trees"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/vl8kx783_20241108_170044.jpg",
    caption: "Handmade beads and wooden ornaments"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/wmiii86k_20241109_091604%20%281%29.jpg",
    caption: "The stunning barn venue in full holiday splendor"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/27b90d6d_1000004321.jpg",
    caption: "Colorful handmade pottery collection"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/0pzviuio_1000004325.jpg",
    caption: "Pet lover apparel and gifts"
  },
];

export default function FestivalOfTreesPage() {
  return (
    <div className="page-transition" data-testid="festival-of-trees-page">
      <SEOHead page="festivalOfTrees" />
      {/* Back Navigation */}
      <div className="bg-emerald-50 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/events" 
            className="inline-flex items-center text-emerald-700 hover:text-emerald-800 transition-colors"
            data-testid="back-to-events"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to AMS Events
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-emerald-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://thebarnatbrambleton.com/wp-content/uploads/2024/08/lowres-bram.jpg')] bg-cover bg-center" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <TreePine className="w-4 h-4" />
                <span>AMS Events Presents</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-semibold text-foreground mb-6 leading-tight">
                Festival of Trees
                <span className="block text-emerald-600">at the Barn</span>
              </h1>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-medium">{eventDetails.dates}</span>
                </div>
                <div className="flex items-center gap-3 text-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span>{eventDetails.times}</span>
                </div>
                <div className="flex items-start gap-3 text-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <span className="font-medium block">{eventDetails.venue}</span>
                    <span className="text-muted-foreground text-base">{eventDetails.address}</span>
                  </div>
                </div>
              </div>

              <FestivalCountdown />

              <div className="mb-6">
                <VendorSpotsFilled variant="light" testId="vendor-spots-filled-hero" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/festival-of-trees/donate-a-tree">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg bg-emerald-600 hover:bg-emerald-700 btn-shine" data-testid="donate-tree-cta">
                    Sponsor a Tree
                    <TreePine className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href={eventDetails.venueWebsite} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="lg" className="rounded-full px-8 py-6 text-lg text-emerald-700 hover:bg-emerald-50" data-testid="venue-website-btn">
                    View Venue
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://thebarnatbrambleton.com/wp-content/uploads/2024/08/TheBarn-Home-Sunset.jpg"
                  alt="The Barn at Brambleton"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                  <p className="text-white font-medium">The Barn at Brambleton</p>
                  <p className="text-white/80 text-sm">A stunning 20-acre venue in Ashburn, VA</p>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-emerald-200/50 rounded-full blur-2xl" />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-red-200/30 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Event Highlights */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-emerald-600 font-accent text-2xl">What to Expect</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              A Holiday Celebration Like No Other
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((highlight, index) => (
              <motion.div
                key={highlight.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <highlight.icon className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3">{highlight.title}</h3>
                <p className="text-muted-foreground">{highlight.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor CTA Section */}
      <section className="py-20 md:py-28 bg-emerald-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <TreePine className="absolute top-10 left-10 w-40 h-40" />
          <TreePine className="absolute bottom-10 right-10 w-32 h-32" />
          <TreePine className="absolute top-1/2 right-1/4 w-24 h-24" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-emerald-200 font-accent text-2xl">For Artisans</span>
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mt-2 mb-6">
                Showcase Your Work at This Premier Event
              </h2>
              <p className="text-xl text-white/80 mb-8">
                Join 50+ artisan vendors at one of Loudoun County's most anticipated holiday events. 
                Connect with thousands of shoppers looking for unique, handcrafted gifts.
              </p>
              <VendorSpotsFilled variant="onGreen" testId="vendor-spots-filled-section" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-xl font-semibold mb-6">Vendor Benefits</h3>
                <ul className="space-y-4">
                  {vendorBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Previous Years Photos */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Camera className="w-4 h-4" />
              <span>Event Gallery</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Photos from Previous Years
            </h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
              Take a look at the magic from our previous Festival of Trees events. Click any photo to view full size.
            </p>
          </motion.div>

          <PhotoGallery photos={previousYearPhotos} columns={3} />
        </div>
      </section>

      {/* Venue Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://thebarnatbrambleton.com/wp-content/uploads/2024/08/lowres-bram.jpg"
                  alt="The Barn at Brambleton interior"
                  className="rounded-2xl w-full h-48 object-cover"
                />
                <img
                  src="https://thebarnatbrambleton.com/wp-content/uploads/2024/08/TheBarn-Home-Stairs.jpg"
                  alt="The Barn at Brambleton stairs"
                  className="rounded-2xl w-full h-48 object-cover"
                />
                <img
                  src="https://thebarnatbrambleton.com/wp-content/uploads/2024/08/TheBarn-Home-Couple.jpg"
                  alt="The Barn at Brambleton exterior"
                  className="rounded-2xl w-full h-48 object-cover col-span-2"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-emerald-600 font-accent text-2xl">The Venue</span>
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2 mb-6">
                The Barn at Brambleton
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                This stunning restored 1940s dairy barn combines rustic sophistication with modern elegance. 
                Set on 20 acres in the heart of Loudoun County wine country, it provides the perfect backdrop 
                for our Festival of Trees celebration.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">{eventDetails.address}</p>
                    <p className="text-muted-foreground text-sm">Minutes from Brambleton Town Center</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(eventDetails.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="rounded-full" data-testid="get-directions-btn">
                    Get Directions
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href={eventDetails.venueWebsite} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" className="rounded-full text-emerald-600" data-testid="venue-info-btn">
                    Learn More About the Venue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <TreePine className="w-16 h-16 mx-auto mb-6 text-emerald-400" strokeWidth={1.5} />
            <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6">
              Be Part of the Festival
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Whether you're a shopper looking for unique gifts or an artisan ready to showcase your work, 
              we can't wait to see you at the Festival of Trees!
            </p>
            <div className="flex justify-center [&>div]:items-center">
              <VendorSpotsFilled variant="dark" testId="vendor-spots-filled-final" />
            </div>
            <p className="text-white/50 mt-6 text-sm">
              Thank you to every artisan who applied. We can't wait to see shoppers at the Festival!
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
